import { Component, Input, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { MATERIAL_IMPORTS } from '../../shared-exports/material.exports';
import { NgClass } from '@angular/common';
import { filter } from 'rxjs';
import { AuthService } from '../../../services/auth-service';
import { AppStoreService } from '../../../store/ngrx-signal.store';
import { ENTITIES } from '../../../store/entities.registry';
import { LoginUserResponse } from '../../../modules/user-profile/user-profile.models';

export interface SidebarContext {
  title: string;
  subtitle?: string;
  icon?: string;
  backLink: string;
  backLabel: string;
}

@Component({
  selector: 'app-sidebar',
  imports: [...MATERIAL_IMPORTS, NgClass, RouterLink],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar {
  isOpen = true;
  activeMenu = '';
  activeRoute = '';
  openSubmenus: Record<string, boolean> = { 'Plan Overview': true };
  showUserMenu = false;

  @Input() menuItems: any;

  /** Optional header for the section the menu belongs to, with a way back out of it. */
  @Input() context: SidebarContext | null = null;

  private readonly currentUrl = signal('');

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly AuthService: AuthService,
  ) {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  isActive(menuItem: any, subItem?: any): boolean {
    const path = this.getRoutePath(menuItem, subItem);
    if (!path) return false;

    let target: string;
    try {
      target = this.router
        .serializeUrl(this.router.createUrlTree([path], { relativeTo: this.route }))
        .split('?')[0];
    } catch {
      return false;
    }

    const url = this.currentUrl().split('?')[0];
    return url === target || url.startsWith(`${target}/`);
  }

  /** A menu item's `data.section`, when it starts a new group of visible items. */
  sectionLabel(menuItem: any, index: number): string | null {
    const section = menuItem?.data?.['section'];
    if (!section) return null;
    const previous = (this.menuItems ?? [])
      .slice(0, index)
      .filter((m: any) => !m?.data?.['isNotAMenuItem'])
      .at(-1);
    return previous?.data?.['section'] === section ? null : section;
  }

  isBranchActive(menuItem: any): boolean {
    if (this.isActive(menuItem)) return true;
    return (menuItem?.children ?? []).some(
      (child: any) => !child?.data?.['isNotAMenuItem'] && this.isActive(menuItem, child),
    );
  }

  private readonly store = inject(AppStoreService);
  userDetails = this.store.entitySingleSignal<LoginUserResponse['me']['data']>(ENTITIES.LOGIN_USER);

  toggleSubmenu(menu: any) {
    const name = menu?.data?.['name'] || menu?.path;
    if (!name) return;

    const isCurrentlyExpanded = this.isSubmenuExpanded(menu);
    this.openSubmenus = {};

    if (!isCurrentlyExpanded) {
      this.openSubmenus[name] = true;
    }

    if (menu?.path && (!menu.children || menu.children.length === 0)) {
      this.navigate(menu.path);
    }
  }

  isSubmenuExpanded(menu: any): boolean {
    const name = menu?.data?.['name'] || menu?.path;
    if (!name) return false;

    if (this.openSubmenus[name] !== undefined) {
      return !!this.openSubmenus[name];
    }

    if (menu?.path && this.router.url.includes(menu.path)) {
      return true;
    }
    return false;
  }

  getRoutePath(menuItem: any, subItem?: any): string {
    const basePath = menuItem.path || '';
    if (!subItem) return basePath;
    const subPath = subItem.path || '';
    if (basePath.startsWith('/') || subPath.startsWith('/')) {
      return `${basePath}/${subPath}`.replace(/\/+/g, '/');
    }
    return `${basePath}/${subPath}`;
  }

  handleMenuClick(menu: any) {
    this.toggleSubmenu(menu);
    this.activeMenu = menu.data?.['name'];
  }

  navigate(route: string) {
    this.activeRoute = route;
    this.router.navigate([route], { relativeTo: this.route });
  }

  toggleUserMenu() {
    this.showUserMenu = !this.showUserMenu;
  }

  navigateTo(route: string) {
    this.showUserMenu = false;
    this.router.navigate(['', route]);
  }

  logout() {
    this.AuthService.logout();
    this.showUserMenu = false;
  }
}
